import * as React from "react";
import {
  HTMLTable,
  Button,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  NonIdealState,
  Dialog,
  FormGroup,
  InputGroup,
  type OverlayToaster,
  Icon,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import ApiAdmin from "../../../api/admin";
import { stickyTableHeaderCss } from "../common/adminCss";
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useInternalCloudAdminGate } from "../useInternalCloudAdminGate";

function AddOrganizationDialog(props: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { isOpen, onClose } = props;

  const [addErrors, setAddErrors] = React.useState<string[]>([]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const createOrganizationMutation = useMutation({
    mutationFn: ({ name }: { name: string }) =>
      ApiAdmin.createOrganization({ name }),
    onMutate: async ({ name }) => {
      await queryClient.cancelQueries({ queryKey: ["organizations"] });
      const previousOrganizations = queryClient.getQueryData(["organizations"]);

      const tempId = Date.now();
      queryClient.setQueryData(["organizations"], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          organizations: [
            ...old.organizations,
            {
              id: tempId,
              name,
            },
          ],
        };
      });

      return { previousOrganizations };
    },
    onError: (err, variables, context) => {
      if (context?.previousOrganizations) {
        queryClient.setQueryData(
          ["organizations"],
          context.previousOrganizations
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message || "Failed to create organization. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Organization created successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  const handleAddSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "add-organization-name"
    ) as HTMLInputElement;

    const name = nameInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }

    if (errors.length === 0) {
      createOrganizationMutation.mutate({
        name: name.trim(),
      });
    } else {
      setAddErrors(errors);
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title="Add Organization"
      isOpen={isOpen}
      onClose={() => {
        if (createOrganizationMutation.isPending === false) {
          setAddErrors([]);
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleAddSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="add-organization-name"
            labelInfo="(required)"
            intent={addErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="add-organization-name"
              intent={addErrors.includes("name") ? "danger" : "none"}
              disabled={createOrganizationMutation.isPending}
              autoFocus
            />
          </FormGroup>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {addErrors.length > 0 &&
              `Missing or invalid fields: ${addErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={createOrganizationMutation.isPending}
              intent="none"
              onClick={() => {
                if (createOrganizationMutation.isPending === false) {
                  setAddErrors([]);
                  onClose();
                }
              }}
            />
            <Button
              text={
                createOrganizationMutation.isPending ? "Creating..." : "Create"
              }
              disabled={createOrganizationMutation.isPending}
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

export default function AdminInternalOrganizations() {
  const gate = useInternalCloudAdminGate();
  const navigate = useNavigate();
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  const [filterText, setFilterText] = React.useState("");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => ApiAdmin.getOrganizations(),
    enabled: gate === "allowed",
  });

  const handleEditOrganization = (organizationId: number) => {
    navigate(`/internal/organizations/${organizationId}/edit`);
  };

  const filteredOrganizations = React.useMemo(() => {
    const filtered =
      data?.organizations.filter((organization) => {
        if (!filterText.trim()) return true;
        const searchText = filterText.toLowerCase();
        return organization.name.toLowerCase().includes(searchText);
      }) || [];

    return filtered.sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [data?.organizations, filterText, sortDirection]);

  if (gate !== "allowed") return null;

  if (isLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        `}
      >
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <NonIdealState
        icon="error"
        title="Error Loading Organizations"
        description={error.message}
      />
    );
  }

  if (data?.organizations.length === 0) {
    return (
      <NonIdealState
        icon="office"
        title="No Organizations Found"
        description="No organizations are available."
      />
    );
  }

  if (filteredOrganizations.length === 0 && filterText.trim()) {
    return (
      <div
        css={css`
          background-color: var(--neutral-6);
          height: 100%;
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: var(--neutral-7);
            border-bottom: 1px solid var(--neutral-5);
          `}
        >
          <InputGroup
            leftIcon="search"
            placeholder="Filter organizations..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            css={css`
              width: 300px;
            `}
          />
          <Button
            icon="add-location"
            text="Add Organization"
            intent="primary"
            onClick={() => setAddDialogOpen(true)}
          />
        </div>
        <NonIdealState
          icon="search"
          title="No Organizations Match Filter"
          description={`No organizations found matching "${filterText}".`}
        />
      </div>
    );
  }

  return (
    <div
      css={css`
        background-color: var(--neutral-6);
        height: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          padding: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: var(--neutral-7);
          border-bottom: 1px solid var(--neutral-5);
        `}
      >
        <InputGroup
          leftIcon="search"
          placeholder="Filter organizations…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          css={css`
            width: 300px;
          `}
        />
        <Button
          icon="add-location"
          text="Add Organization"
          intent="primary"
          onClick={() => setAddDialogOpen(true)}
        />
      </div>
      <div
        css={css`
          flex: 1;
          overflow: auto;
        `}
      >
        <HTMLTable
          striped
          interactive
          css={[
            stickyTableHeaderCss,
            css`
              width: 100%;
            `,
          ]}
        >
          <colgroup>
            <col />
            <col width="64px" />
          </colgroup>
          <thead>
            <tr>
              <th
                onClick={() =>
                  setSortDirection(sortDirection === "asc" ? "desc" : "asc")
                }
                css={css`
                  cursor: pointer;
                  user-select: none;

                  &:hover {
                    background-color: var(--background-dark-translucent);
                  }
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding-left: 5px;
                  `}
                >
                  <span>Name</span>
                  <Icon
                    icon={
                      sortDirection === "asc" ? "chevron-up" : "chevron-down"
                    }
                  />
                </div>
              </th>
              <th
                css={css`
                  width: 64px;
                `}
              />
            </tr>
          </thead>
          <tbody>
            {filteredOrganizations.map((organization) => (
              <tr
                key={organization.id}
                onClick={() => handleEditOrganization(organization.id)}
              >
                <td>
                  <div
                    css={css`
                      height: 30px;
                      display: flex;
                      align-items: center;
                      padding-left: 5px;
                    `}
                  >
                    {organization.name}
                  </div>
                </td>
                <td>
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    css={css`
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      height: 30px;
                      width: 100%;
                    `}
                  >
                    <Popover
                      content={
                        <Menu>
                          <MenuItem
                            icon="settings"
                            text="Settings"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/internal/organizations/${organization.id}/edit?tab=settings`
                              );
                            }}
                          />
                          <MenuItem
                            icon="unresolve"
                            text="Manage Materials"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/internal/organizations/${organization.id}/edit?tab=materials`
                              );
                            }}
                          />
                          <MenuItem
                            icon="map-marker"
                            text="Manage Sites"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/internal/organizations/${organization.id}/edit?tab=sites`
                              );
                            }}
                          />
                          <MenuItem
                            icon="cube"
                            text="Manage Order Types"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/internal/organizations/${organization.id}/edit?tab=orderTypes`
                              );
                            }}
                          />
                          <MenuItem
                            icon="shield"
                            text="Manage Users"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/admin/organizations/${organization.id}/users`
                              );
                            }}
                          />
                        </Menu>
                      }
                      placement="bottom-end"
                    >
                      <Button size="small" icon="more" />
                    </Popover>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </HTMLTable>
      </div>
      <AddOrganizationDialog
        key={
          addDialogOpen
            ? "add-organization-dialog"
            : "add-organization-dialog-closed"
        }
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      />
    </div>
  );
}
